/**
 * Direct routing - the priority-ordered fallback chain applied at claim
 * creation. Rows are drag-reorderable (dnd-kit, keyboard-accessible); the
 * first rule whose target can actually be assigned wins, so row order IS
 * the policy.
 */

import { useCallback, useMemo, useState } from 'react'
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core'
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { GripVertical, Pencil, Plus, Route, Trash2 } from 'lucide-react'
import { toast } from 'sonner'

import { Badge } from '@/shared/components/ui/badge'
import { Button } from '@/shared/components/ui/button'
import { ConfirmDeleteDialog } from '@/admin/components/confirm-delete-dialog'
import { toastApiError } from '@/shared/lib/api-error'
import { cn } from '@/shared/lib/utils'
import {
  useDeleteRoutingRule,
  useReorderRoutingRules,
  type RoutingMeta,
  type RoutingRule,
} from '@/admin/hooks/use-routing-rules'
import { AssigneeChip, StrategyChip, WorkflowScopeBadge } from './shared'

interface DirectRoutingSectionProps {
  rules: RoutingRule[]
  meta: RoutingMeta
  onAdd: () => void
  onEdit: (rule: RoutingRule) => void
}

export function DirectRoutingSection({
  rules,
  meta,
  onAdd,
  onEdit,
}: DirectRoutingSectionProps) {
  const reorder = useReorderRoutingRules()
  const remove = useDeleteRoutingRule()
  const [ruleToDelete, setRuleToDelete] = useState<RoutingRule | null>(null)

  const sorted = useMemo(
    () =>
      [...rules].sort(
        (a, b) => b.priority - a.priority || a.key.localeCompare(b.key),
      ),
    [rules],
  )

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  )

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event
      if (!over || active.id === over.id) return
      const oldIndex = sorted.findIndex((r) => r.uuid === active.id)
      const newIndex = sorted.findIndex((r) => r.uuid === over.id)
      if (oldIndex === -1 || newIndex === -1) return
      const reordered = [...sorted]
      const [moved] = reordered.splice(oldIndex, 1)
      reordered.splice(newIndex, 0, moved)
      reorder.mutate(
        { kind: 'direct', ordered_uuids: reordered.map((r) => r.uuid) },
        { onError: (err) => toastApiError(err, 'Failed to reorder rules') },
      )
    },
    [sorted, reorder],
  )

  return (
    <section className="space-y-3" data-testid="direct-routing-section">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h3 className="text-[15px] font-medium text-[var(--ink)]">
            Direct routing
          </h3>
          <p className="text-[12px] text-[var(--ink-3)]">
            Who gets a claim the moment it is created. Tried top to bottom -
            the first rule that can assign someone wins; drag to reorder.
          </p>
        </div>
        <Button onClick={onAdd} data-testid="add-direct-rule">
          <Plus className="h-3.5 w-3.5" />
          Add rule
        </Button>
      </div>

      {sorted.length === 0 ? (
        <div className="flex flex-col items-center gap-2 rounded-lg border-[0.5px] border-[var(--rule)] bg-[var(--bg-light)] px-6 py-10 text-center">
          <Route className="h-5 w-5 text-[var(--ink-3)]" />
          <p className="text-[13px] text-[var(--ink-2)]">
            No direct routing rules yet.
          </p>
          <p className="text-[12px] text-[var(--ink-3)]">
            Add a rule to decide who new claims go to. Until then, new claims
            stay unassigned.
          </p>
        </div>
      ) : (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={sorted.map((r) => r.uuid)}
            strategy={verticalListSortingStrategy}
          >
            <div className="overflow-hidden rounded-lg border-[0.5px] border-[var(--rule)] bg-[var(--bg-light)]">
              {sorted.map((rule, index) => (
                <SortableDirectRuleRow
                  key={rule.uuid}
                  rule={rule}
                  index={index}
                  isLast={index === sorted.length - 1}
                  meta={meta}
                  onEdit={() => onEdit(rule)}
                  onDelete={() => setRuleToDelete(rule)}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      )}

      <ConfirmDeleteDialog
        open={ruleToDelete !== null}
        onOpenChange={(next) => !next && setRuleToDelete(null)}
        title="Delete routing rule?"
        description={
          ruleToDelete
            ? `"${ruleToDelete.name}" will no longer route new claims.`
            : undefined
        }
        isPending={remove.isPending}
        onConfirm={() => {
          if (!ruleToDelete) return
          remove.mutate(ruleToDelete.uuid, {
            onSuccess: () => {
              setRuleToDelete(null)
              toast.success('Routing rule deleted')
            },
            onError: (err) => toastApiError(err, 'Failed to delete the rule'),
          })
        }}
      />
    </section>
  )
}

function SortableDirectRuleRow({
  rule,
  index,
  isLast,
  meta,
  onEdit,
  onDelete,
}: {
  rule: RoutingRule
  index: number
  isLast: boolean
  meta: RoutingMeta
  onEdit: () => void
  onDelete: () => void
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: rule.uuid })

  return (
    <div
      ref={setNodeRef}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
      }}
      data-testid={`direct-rule-row-${rule.key}`}
      className={cn(
        'group flex items-center gap-3 bg-[var(--bg)] px-4 py-3',
        !isLast && 'border-b-[0.5px] border-b-[var(--rule)]',
      )}
    >
      <button
        type="button"
        {...attributes}
        {...listeners}
        aria-label={`Reorder rule ${rule.name}`}
        data-testid={`direct-rule-grip-${rule.key}`}
        className="flex h-5 w-5 shrink-0 cursor-grab items-center justify-center text-[var(--ink-3)] hover:text-[var(--ink)] active:cursor-grabbing"
      >
        <GripVertical className="h-3.5 w-3.5" />
      </button>
      <span className="w-5 shrink-0 text-center text-[11px] text-[var(--ink-3)]">
        {index + 1}
      </span>

      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-[13px] text-[var(--ink)]">{rule.name}</span>
          {!rule.is_active && <Badge variant="outline">Paused</Badge>}
          <WorkflowScopeBadge
            workflowKey={rule.workflow_key}
            workflows={meta.workflows ?? []}
          />
        </div>
        <div className="mt-1 flex flex-wrap items-center gap-1.5">
          <AssigneeChip assignee={rule.assignee} />
          {rule.assignee.kind === 'role' && (
            <StrategyChip
              strategy={rule.strategy}
              strategyOptions={meta.strategy_options}
            />
          )}
        </div>
      </div>

      <div className="flex items-center gap-1 opacity-0 transition-opacity duration-150 group-hover:opacity-100 group-focus-within:opacity-100">
        <Button
          variant="ghost"
          size="icon-sm"
          aria-label={`Edit ${rule.name}`}
          onClick={onEdit}
        >
          <Pencil className="h-3.5 w-3.5" />
        </Button>
        <Button
          variant="ghost"
          size="icon-sm"
          aria-label={`Delete ${rule.name}`}
          onClick={onDelete}
        >
          <Trash2 className="h-3.5 w-3.5" />
        </Button>
      </div>
    </div>
  )
}
